/**
 * Stable identity for `sections.pass.bevelPasses[]` entries.
 *
 * Each entry today is index-keyed only — `activeBevelIndex` plus array
 * position. That makes per-bevel report scoping fragile: reordering or
 * deleting bevels silently re-attaches historical reports to the wrong
 * physical procedure. This module stamps a stable string `id` on each
 * entry the first time the Pass Sequence form normalises a payload,
 * and exposes lookup helpers that the report-history consumers use to
 * resolve a `bevelId` back to its current array index.
 *
 * Pure functions only — no React, no IO. The stamping is *lazy*: it
 * happens at the normalisation boundary in `WpsPassDetails`, so the
 * ids land in persisted `sections` on the next user-triggered save.
 *
 * Added in `wps-history-bevel-scoped` issue 01.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function hasStringId(value: unknown): value is { id: string } {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    value.id.trim().length > 0
  )
}

/**
 * Generate a stable string id. Prefers `crypto.randomUUID()` when
 * available (modern browsers + Node 14.17+); falls back to a
 * timestamp + random suffix on older runtimes.
 */
function newBevelId(): string {
  try {
    const g = globalThis as { crypto?: { randomUUID?: () => string } }
    if (g.crypto && typeof g.crypto.randomUUID === 'function') {
      return g.crypto.randomUUID()
    }
  } catch {
    // ignore — fall through to fallback
  }
  return `bvl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Stamps a new id on every entry that lacks one (or whose `id` is
 * blank/whitespace). Entries with a valid string id pass through
 * unchanged, so calling this repeatedly is idempotent. Returns a new
 * array; does not mutate the input.
 */
export function stampBevelIdsIfMissing<T extends Record<string, unknown>>(
  passes: T[]
): T[] {
  if (!Array.isArray(passes)) return []
  return passes.map((entry) => {
    if (hasStringId(entry)) return entry
    return { ...entry, id: newBevelId() } as T
  })
}

/**
 * Read the id at a specific array index from a raw section payload.
 * Returns `undefined` for malformed sections, out-of-range indices,
 * or entries that lack a string id.
 */
export function getBevelIdAtIndex(
  passSection: unknown,
  index: number
): string | undefined {
  if (!isRecord(passSection)) return undefined
  const arr = passSection.bevelPasses
  if (!Array.isArray(arr)) return undefined
  if (!Number.isInteger(index) || index < 0 || index >= arr.length) {
    return undefined
  }
  const entry = arr[index]
  if (!hasStringId(entry)) return undefined
  return entry.id
}

/**
 * Resolve a stable `bevelId` back to the current array index. Returns
 * `-1` when the id is unknown, the section is malformed, or `bevelId`
 * is empty. Never throws.
 */
export function findIndexByBevelId(
  passSection: unknown,
  bevelId: string
): number {
  if (typeof bevelId !== 'string' || bevelId.trim().length === 0) return -1
  if (!isRecord(passSection)) return -1
  const arr = passSection.bevelPasses
  if (!Array.isArray(arr)) return -1
  for (let i = 0; i < arr.length; i += 1) {
    const entry = arr[i]
    if (hasStringId(entry) && entry.id === bevelId) return i
  }
  return -1
}

/**
 * List every `(id, index)` pair in the section, preserving array
 * order. Entries that lack a string id are skipped (they have no
 * stable identity yet — they'll get one on the next normalise/save).
 */
export function listBevelsWithIds(
  passSection: unknown
): Array<{ id: string; index: number }> {
  if (!isRecord(passSection)) return []
  const arr = passSection.bevelPasses
  if (!Array.isArray(arr)) return []
  const out: Array<{ id: string; index: number }> = []
  for (let i = 0; i < arr.length; i += 1) {
    const entry = arr[i]
    if (hasStringId(entry)) out.push({ id: entry.id, index: i })
  }
  return out
}
